'use client';

import { usePathname, useRouter } from 'next/navigation';
import { defaultLocale, locales, type Locale } from '@/i18n';

const DEFAULT_UNPREFIXED_LOCALE: Locale = defaultLocale;

const LOCALIZED_PATH_PATTERN = /^\/([a-z]{2}(?:-[A-Z]{2})?)(\/|$)/;
const STATIC_EXPORT_INDEX_PATTERN = /\/index\.(?:html|txt)$/i;

function normalizePath(path: string): string {
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.replace(/\/{2,}/g, '/');
}

function splitPathAndSuffix(input: string): { pathname: string; suffix: string } {
  const match = input.match(/^([^?#]*)([?#].*)?$/);
  if (!match) return { pathname: input, suffix: '' };
  return { pathname: match[1] || '/', suffix: match[2] || '' };
}

function stripStaticExportIndex(pathname: string): string {
  const normalizedPathname = normalizePath(pathname || '/');
  const withoutIndex = normalizedPathname.replace(STATIC_EXPORT_INDEX_PATTERN, '/');
  return normalizePath(withoutIndex || '/');
}

export function normalizeAppPath(path: string): string {
  const { pathname, suffix } = splitPathAndSuffix(path);
  const normalizedPathname = stripStaticExportIndex(pathname || '/');
  return `${normalizedPathname}${suffix}`;
}

export function getLocaleFromPathname(pathname: string): Locale {
  const normalizedPathname = stripStaticExportIndex(pathname || '/');
  const localeCandidate = normalizedPathname.match(LOCALIZED_PATH_PATTERN)?.[1];

  if (localeCandidate && locales.includes(localeCandidate as Locale)) {
    return localeCandidate as Locale;
  }

  return DEFAULT_UNPREFIXED_LOCALE;
}

export function stripLocalePrefix(pathname: string): string {
  const normalizedPathname = stripStaticExportIndex(pathname || '/');
  const localeCandidate = normalizedPathname.match(LOCALIZED_PATH_PATTERN)?.[1];

  if (!localeCandidate || !locales.includes(localeCandidate as Locale)) {
    return normalizedPathname;
  }

  const strippedPath = normalizedPathname.replace(LOCALIZED_PATH_PATTERN, '/');
  return strippedPath === '' ? '/' : normalizePath(strippedPath);
}

export function createLocalePath(path: string, locale: string): string {
  const normalizedPath = normalizeAppPath(path);
  const { pathname, suffix } = splitPathAndSuffix(normalizedPath);
  const cleanPath = stripLocalePrefix(pathname);

  if (locale === DEFAULT_UNPREFIXED_LOCALE) {
    return `${cleanPath}${suffix}`;
  }

  return `/${locale}${cleanPath}${suffix}`;
}

function isExternalUrl(path: string): boolean {
  return /^(?:https?:\/\/|mailto:|tel:)/i.test(path);
}

export function useLocaleNavigation() {
  const router = useRouter();
  const pathname = usePathname();

  const locale = getLocaleFromPathname(pathname || '/');

  const navigate = (path: string): string => {
    if (isExternalUrl(path)) return path;
    return createLocalePath(path, locale);
  };

  const navigateTo = (path: string, options?: { replace?: boolean }): void => {
    if (isExternalUrl(path)) {
      window.location.assign(path);
      return;
    }

    const fullPath = navigate(path);
    if (options?.replace) {
      router.replace(fullPath);
      return;
    }

    router.push(fullPath);
  };

  return { locale, navigate, navigateTo };
}
